/**
 * @file     FileDropzone.tsx
 * @purpose  Drag-and-drop file upload component with click-to-browse, file list, and remove capability.
 */
'use client';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';

type FileDropzoneProps = {
  value: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeBytes?: number;
  accept?: string;
  label?: string;
  /** Overrides the MIME check, e.g. for HEIC photos that arrive with an empty type. */
  isAllowed?: (file: File) => boolean;
  /** Replaces the default hint and limits lines. */
  hint?: string;
  limitsLabel?: string;
  /** Hides the selected-file list when the caller renders its own (an upload grid). */
  hideList?: boolean;
};

const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_ACCEPT = 'image/*,.pdf,.doc,.docx';

const ALLOWED_MIME_PREFIXES = ['image/'];
const ALLOWED_MIME_EXACT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function isAllowedType(file: File): boolean {
  if (ALLOWED_MIME_PREFIXES.some((p) => file.type.startsWith(p))) return true;
  if (ALLOWED_MIME_EXACT.includes(file.type)) return true;
  return false;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileDropzone = ({
  value,
  onChange,
  maxFiles = DEFAULT_MAX_FILES,
  maxSizeBytes = DEFAULT_MAX_SIZE,
  accept = DEFAULT_ACCEPT,
  label,
  isAllowed = isAllowedType,
  hint,
  limitsLabel,
  hideList = false,
}: FileDropzoneProps) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      setError(null);
      const arr = Array.from(incoming);
      const remaining = maxFiles - value.length;
      if (remaining <= 0) {
        setError(t('dropzone.maxFilesReached', { defaultValue: 'Maximum {{max}} files allowed', max: maxFiles }));
        return;
      }

      const valid: File[] = [];
      for (const file of arr.slice(0, remaining)) {
        if (!isAllowed(file)) {
          setError(t('dropzone.invalidType', { defaultValue: '{{name}} is not an allowed file type', name: file.name }));
          continue;
        }
        if (file.size > maxSizeBytes) {
          setError(t('dropzone.tooLarge', { defaultValue: '{{name}} exceeds {{max}} limit', name: file.name, max: formatSize(maxSizeBytes) }));
          continue;
        }
        valid.push(file);
      }

      if (valid.length > 0) {
        onChange([...value, ...valid]);
      }
    },
    [value, onChange, maxFiles, maxSizeBytes, t, isAllowed],
  );

  const removeFile = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      setError(null);
    },
    [value, onChange],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleClick = () => inputRef.current?.click();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {label ? (
        <Typography sx={{ fontSize: theme.typography.bodySm, fontWeight: 500, color: theme.colors.textPrimary }}>
          {label}
        </Typography>
      ) : null}

      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 3,
          px: 2,
          borderRadius: `${theme.radius.md}px`,
          border: `2px dashed ${isDragOver ? theme.colors.primary : alpha(theme.rawColors.border, 0.6)}`,
          backgroundColor: isDragOver ? alpha(theme.rawColors.primary, 0.06) : alpha(theme.rawColors.surfaceMuted, 0.4),
          cursor: 'pointer',
          transition: `border-color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}, background-color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}`,
          '&:hover': {
            borderColor: theme.colors.primary,
            backgroundColor: alpha(theme.rawColors.primary, 0.04),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.colors.primary}`,
            outlineOffset: '2px',
          },
        }}
      >
        <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.colors.textSecondary, textAlign: 'center' }}>
          {hint ?? t('dropzone.hint', { defaultValue: 'Drag & drop files here, or click to browse' })}
        </Typography>
        <Typography sx={{ fontSize: 12, color: alpha(theme.rawColors.textSecondary, 0.7) }}>
          {limitsLabel ?? t('dropzone.limits', { defaultValue: 'Up to {{max}} files, {{size}} each', max: maxFiles, size: formatSize(maxSizeBytes) })}
        </Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        aria-label={t('dropzone.inputLabel', { defaultValue: 'Choose files to upload' })}
      />

      {error ? (
        <Typography sx={{ fontSize: 12, color: theme.colors.error }}>{error}</Typography>
      ) : null}

      {!hideList && value.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {value.map((file, i) => (
            <Box
              key={`${file.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: 1,
                borderRadius: `${theme.radius.sm}px`,
                backgroundColor: alpha(theme.rawColors.surfaceMuted, 0.5),
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 13,
                  color: theme.colors.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: theme.colors.textSecondary, flexShrink: 0 }}>
                {formatSize(file.size)}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                aria-label={t('dropzone.removeFile', { defaultValue: 'Remove {{name}}', name: file.name })}
                sx={{ minWidth: 44, minHeight: 44, color: theme.colors.textSecondary, '&:hover': { color: theme.colors.error } }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
